import React, { useMemo, useRef } from 'react';
import { Button, FormField, Search, StatusType } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import s from './DomainInput.scss';
import { DomainSearchStatus, useDomainSearch } from '../../hooks/useDomainSearch';
import { useBiLogger } from '../../../../hooks/useBiLogger/useBiLogger';
import { BI_CONSTANTS } from '../../../../hooks/useBiLogger/biConstants';

interface DomainInputProps {
  value: string;
  onChange: (value: string) => void;
}

export function DomainInput({ value, onChange }: DomainInputProps) {
  const { t } = useTranslation();
  const biLogger = useBiLogger();
  const { searchDomain, isSearching, searchStatus, resetSearch } = useDomainSearch(value);

  const lastSearchValue = useRef<string>();

  const status = useMemo<StatusType>(() => {
    if (searchStatus === DomainSearchStatus.Unavailable || searchStatus === DomainSearchStatus.Unsupported) {
      return 'error';
    }
  }, [searchStatus]);

  const statusMessage = useMemo(() => {
    switch (searchStatus) {
      case DomainSearchStatus.Unavailable:
        return t('DOMAIN_PAGE_SEARCH_STATUS_UNAVAILABLE', { domain: lastSearchValue.current });
      case DomainSearchStatus.Unsupported:
        return t('DOMAIN_PAGE_SEARCH_STATUS_UNSUPPORTED', { domain: lastSearchValue.current });
    }
  }, [searchStatus, t]);

  const onSearchClick = () => {
    biLogger.actionClicked({
      action: BI_CONSTANTS.ACTION.DOMAIN_PAGE_SEARCH,
      button_name: BI_CONSTANTS.BUTTON_NAME.SEARCH,
      value,
    });
    lastSearchValue.current = value;
    searchDomain();
  };

  const onSearchClear = () => {
    lastSearchValue.current = undefined;
    resetSearch();
    onChange('');
  };

  return (
    <div className={s.domainInputWrapper}>
      <FormField statusMessage={statusMessage} status={status}>
        <Search
          status={isSearching ? 'loading' : undefined}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('DOMAIN_PAGE_SEARCH_INPUT_PLACEHOLDER')}
          className={s.domainInput}
          onClear={onSearchClear}
          value={value}
          size="large"
          required
        />
      </FormField>
      <Button size="large" priority="secondary" onClick={onSearchClick} disabled={!value}>
        {t('DOMAIN_PAGE_SEARCH_BUTTON')}
      </Button>
    </div>
  );
}
